import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Camera, CheckCircle2, MapPin, Star } from "lucide-react";

const SCENE = "https://images.unsplash.com/photo-1462539405390-d0bdb635c7d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

const Corner = ({ pos }) => {
  const map = {
    tl: "top-3 left-3 border-t-2 border-l-2 rounded-tl-md",
    tr: "top-3 right-3 border-t-2 border-r-2 rounded-tr-md",
    bl: "bottom-16 left-3 border-b-2 border-l-2 rounded-bl-md",
    br: "bottom-16 right-3 border-b-2 border-r-2 rounded-br-md",
  };
  return <span className={`absolute w-7 h-7 border-[#D4AF37] ${map[pos]}`} />;
};

const Step = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-2.5">
    <span className="w-8 h-8 rounded-full bg-[#D4AF37]/15 text-[#B8901F] flex items-center justify-center shrink-0">
      <Icon size={15} />
    </span>
    <div className="min-w-0">
      <p className="text-sm font-bold text-[#0F0F0F] leading-none">{title}</p>
      <p className="text-[11px] text-[#687280] mt-1 leading-tight">{sub}</p>
    </div>
  </div>
);

export const ScannerPromo = () => {
  const navigate = useNavigate();
  const goScan = () => navigate("/ai-resources/opportunity-scanner");

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-12" data-testid="home-scanner-promo">
      <div className="relative rounded-[2rem] border border-gray-100 bg-white shadow-[0_4px_24px_rgb(0,0,0,0.04)] px-6 md:px-10 py-8 overflow-hidden">
        {/* NEW badge */}
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE9FE] text-[#7C3AED] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide">
            <Sparkles size={12} /> New AI Feature
          </span>
          <ArrowRight size={16} className="text-[#7C3AED]" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_230px_0.85fr] gap-8 lg:gap-6 items-center">
          {/* Left: title + steps */}
          <div>
            <div className="flex items-start gap-4">
              <span className="relative w-16 h-16 rounded-2xl bg-[#0F0F0F] flex items-center justify-center shrink-0 shadow-md">
                <Camera size={26} className="text-[#D4AF37]" />
                <span className="absolute inset-1.5 rounded-xl border border-[#D4AF37]/40" />
                <span className="absolute -bottom-1.5 -right-1.5 text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">AI</span>
              </span>
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F0F0F] leading-tight">
                  AI Opportunity <span className="text-[#D4AF37]">Scanner</span>
                </h2>
                <p className="text-[#4b5563] mt-2 text-sm md:text-base">See a problem. Snap a photo. Discover opportunities with AI.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-2 mt-7">
              <Step icon={CheckCircle2} title="Snap" sub="Take a photo of anything" />
              <ArrowRight size={16} className="hidden sm:block text-[#D4AF37] shrink-0" />
              <Step icon={MapPin} title="AI Scans" sub="Finds problems & gaps" />
              <ArrowRight size={16} className="hidden sm:block text-[#D4AF37] shrink-0" />
              <Step icon={Star} title="Get Ideas" sub="High-potential startup ideas" />
            </div>
          </div>

          {/* Center: phone mockup */}
          <div className="flex justify-center">
            <div className="relative w-[210px] h-[420px] rounded-[2.2rem] bg-[#0F0F0F] p-2.5 shadow-[0_25px_60px_rgba(0,0,0,0.28)]">
              <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0F0F0F] rounded-b-2xl z-10" />
              <div className="w-full h-full rounded-[1.8rem] overflow-hidden relative">
                <img src={SCENE} alt="Scan a real-world scene" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/10" />
                <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
                <Star size={16} className="absolute top-4 right-5 text-[#D4AF37] fill-[#D4AF37]" />
                {/* shutter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white/90 border-4 border-white shadow-lg flex items-center justify-center">
                  <span className="w-8 h-8 rounded-full bg-white border border-gray-300" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: CTA */}
          <div className="text-center lg:text-left">
            <p className="text-lg md:text-xl font-semibold text-[#0F0F0F] leading-snug">
              Turn anything you see into your next startup.
            </p>
            <div className="relative mt-5 flex items-center justify-center lg:justify-start gap-3">
              <svg width="46" height="40" viewBox="0 0 46 40" className="text-[#7C3AED] shrink-0" fill="none">
                <path d="M4 4 C 6 22, 20 32, 40 30" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="1 0" />
                <path d="M33 24 L 41 30 L 32 34" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <button
                onClick={goScan}
                data-testid="home-scanner-scan-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#6D28D9] to-[#8B5CF6] text-white px-8 py-3.5 text-base font-extrabold shadow-[0_12px_30px_rgba(109,40,217,0.35)] hover:shadow-[0_16px_38px_rgba(109,40,217,0.45)] hover:-translate-y-0.5 transition-all"
              >
                SCAN <ArrowRight size={18} />
              </button>
            </div>
            <p className="text-xs text-[#9aa1ab] mt-3 lg:pl-14">Unlock with Pro Access</p>
          </div>
        </div>
      </div>
    </section>
  );
};
